/** Pagina Operatori — rubrica soggetti . */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PlusCircle, Pencil, Trash2, ShieldCheck, Truck } from "lucide-react";
import { endpoints, type ContactItem } from "@/api/client";
import { OperatoreDialog } from "@/components/OperatoreDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { CenteredLoader, ErrorState, EmptyState } from "@/components/ui/feedback";
import { RUOLO_CONTATTO } from "@/lib/rentriChoices";

const ROLES = ["produttore", "trasportatore", "destinatario", "intermediario"] as const;

function roleLabel(role: string): string {
  const opt = RUOLO_CONTATTO.find((r) => r.value === role);
  return opt?.label ?? role;
}

export function Contacts() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [role, setRole] = useState(() => searchParams.get("ruolo") ?? "");
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [edit, setEdit] = useState<ContactItem | null>(null);

  useEffect(() => {
    const r = searchParams.get("ruolo");
    if (r && ROLES.includes(r as (typeof ROLES)[number])) setRole(r);
    if (searchParams.get("nuovo") === "1") {
      setEdit(null);
      setDialogOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, []);

  const list = useQuery({
    queryKey: ["contacts", role, q],
    queryFn: () =>
      endpoints.contacts.list({ role: role || undefined, q: q || undefined }),
  });

  const verify = useMutation({
    mutationFn: (id: string) => endpoints.contacts.verify(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      if (r.rentri_status >= 400) {
        toast.error(`Verifica RENTRI fallita (${r.rentri_status})`, {
          description:
            typeof r.result === "string"
              ? r.result
              : JSON.stringify(r.result ?? "").slice(0, 120),
        });
      } else {
        toast.success(`Verifica completata (RENTRI ${r.rentri_status})`);
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => endpoints.contacts.remove(id),
    onSuccess: () => {
      toast.success("Operatore rimosso");
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const openNew = () => {
    setEdit(null);
    setDialogOpen(true);
  };

  const openEdit = (c: ContactItem) => {
    setEdit(c);
    setDialogOpen(true);
  };

  const handleRemove = (c: ContactItem) => {
    if (
      !window.confirm(
        `Rimuovere ${c.denominazione} dalla rubrica operatori?\nI movimenti già inviati non sono modificati.`,
      )
    ) {
      return;
    }
    remove.mutate(c.id);
  };

  const items = list.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Truck className="h-7 w-7" /> Operatori
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl">
            Produttori, trasportatori, destinatari e intermediari usati nei movimenti e nei
            formulari. Aggiungili prima di compilare registro e FIR.
          </p>
        </div>
        <Button onClick={openNew}>
          <PlusCircle className="mr-2 h-4 w-4" /> Nuovo operatore
        </Button>
      </div>

      <Card>
        <CardHeader className="space-y-3">
          <CardTitle className="text-base">Rubrica operatori</CardTitle>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              placeholder="Cerca denominazione, CF, P.IVA, iscrizione…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="sm:max-w-md"
            />
            <div className="flex flex-wrap gap-1">
              <Button
                size="sm"
                variant={role === "" ? "default" : "outline"}
                onClick={() => setRole("")}
              >
                Tutti
              </Button>
              {ROLES.map((r) => (
                <Button
                  key={r}
                  size="sm"
                  variant={role === r ? "default" : "outline"}
                  onClick={() => setRole(r)}
                >
                  {roleLabel(r)}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {list.isLoading && <CenteredLoader />}
          {list.error && <ErrorState error={list.error} />}
          {!list.isLoading && !list.error && items.length === 0 && (
            <div className="py-12 text-center space-y-4">
              <EmptyState
                title="Nessun operatore"
                description="Aggiungi i soggetti che usi nei movimenti e nei formulari per selezionarli dagli elenchi."
                icon={<Truck className="h-8 w-8" />}
              />
              <Button onClick={openNew} variant="outline">
                <PlusCircle className="mr-2 h-4 w-4" /> Aggiungi il primo operatore
              </Button>
            </div>
          )}
          {items.length > 0 && (
            <Table>
              <THead>
                <TR>
                  <TH>Ruolo</TH>
                  <TH>Denominazione</TH>
                  <TH>P.IVA / CF</TH>
                  <TH>Iscrizione</TH>
                  <TH>Verifica</TH>
                  <TH className="text-right">Azioni</TH>
                </TR>
              </THead>
              <TBody>
                {items.map((c: ContactItem) => (
                  <TR
                    key={c.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => openEdit(c)}
                  >
                    <TD>
                      <Badge variant="outline">{roleLabel(c.role)}</Badge>
                    </TD>
                    <TD className="font-medium max-w-[240px] truncate" title={c.denominazione}>
                      {c.denominazione}
                    </TD>
                    <TD className="font-mono text-xs whitespace-nowrap">
                      {c.partita_iva ?? c.codice_fiscale ?? "—"}
                    </TD>
                    <TD className="font-mono text-xs">{c.rentri_iscrizione ?? "—"}</TD>
                    <TD>
                      {c.rentri_check_at ? (
                        <Badge variant="success">verificato</Badge>
                      ) : (
                        <Badge variant="secondary">—</Badge>
                      )}
                    </TD>
                    <TD
                      className="text-right whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {c.rentri_iscrizione && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8"
                          onClick={() => verify.mutate(c.id)}
                          title="Verifica iscrizione RENTRI"
                        >
                          <ShieldCheck className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8"
                        onClick={() => openEdit(c)}
                      >
                        <Pencil className="mr-1 h-3.5 w-3.5" />
                        Modifica
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 text-destructive hover:text-destructive"
                        onClick={() => handleRemove(c)}
                        disabled={remove.isPending}
                      >
                        <Trash2 className="mr-1 h-3.5 w-3.5" />
                        Elimina
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <OperatoreDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        contact={edit}
        defaultRole={role || undefined}
        onSaved={() => qc.invalidateQueries({ queryKey: ["contacts"] })}
      />
    </div>
  );
}
